"use client";

import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronDown, BookOpen, GraduationCap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/lib/i18n/LanguageProvider";
import { track, trackAndOpen } from "@/lib/tracking";
import { useTrackSection } from "@/hooks/useTrackSection";

const Hero = () => {
  const { t } = useI18n();
  const sectionRef = useTrackSection<HTMLElement>("hero");
  const [studentOpen, setStudentOpen] = useState(false);
  const studentMenuRef = useRef<HTMLDivElement>(null);

  // Touch has no hover, so the menu is dismissed by tapping away or Escape.
  useEffect(() => {
    if (!studentOpen) return;
    const onDocDown = (e: PointerEvent) => {
      if (!studentMenuRef.current?.contains(e.target as Node))
        setStudentOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setStudentOpen(false);
    };
    document.addEventListener("pointerdown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [studentOpen]);

  const handleCtaClick = () => {
    trackAndOpen(t.links.teacherHero, {
      ctaLabel: t.nav.teacherAccount,
      ctaLocation: "hero",
      linkType: "signup",
    });
  };

  // Both destinations are other products, not teacher.teado.ai — so these are
  // plain cta_click events, no external_link_to_teacher (which exists to stitch
  // the teacher-app session). cta_location splits the two branches.
  const openStudentDemo = (branch: "k12" | "ielts") => {
    const url =
      branch === "ielts" ? t.links.studentDemoIelts : t.links.studentDemo;
    track("cta_click", {
      cta_label:
        branch === "ielts" ? t.nav.studentLevels.ielts : t.nav.studentLevels.k12,
      cta_location: `hero_student_demo_${branch}`,
      target_url: url,
    });
    window.open(url, "_blank", "noopener,noreferrer");
    setStudentOpen(false);
  };

  const studentBranches = [
    {
      key: "k12" as const,
      label: t.nav.studentLevels.k12,
      Icon: BookOpen,
      iconBg: "bg-emerald-100 text-emerald-600",
    },
    {
      key: "ielts" as const,
      label: t.nav.studentLevels.ielts,
      Icon: GraduationCap,
      iconBg: "bg-indigo-100 text-indigo-600",
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-gradient-to-br from-background via-background to-secondary/20 lg:min-h-[calc(100vh-11rem)] flex items-center py-10 lg:py-12"
    >
      <div className="container mx-auto px-4">
        {/* Two columns from lg up so the copy + both CTAs stay above the fold
            next to the demo. Below lg it stacks, with the CTAs ahead of the
            demo so they are still reachable without scrolling. */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-center max-w-7xl mx-auto">
          <div className="text-center lg:text-left">
            <h1 className="text-3xl md:text-4xl xl:text-5xl font-bold mb-4 text-balance">
              <span className="text-foreground">{t.hero.titleRest}</span>
            </h1>
            <div className="mb-6 text-sm md:text-base xl:text-lg text-foreground/90">
              <p>
                <span className="gradient-text font-bold">
                  {t.hero.descHighlight}
                </span>
                {t.hero.descPost}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center lg:justify-start items-stretch sm:items-center">
              <Button
                size="lg"
                onClick={handleCtaClick}
                className="bg-foreground text-background hover:bg-foreground/90 transition-opacity text-sm md:text-base px-5 py-3 h-auto whitespace-nowrap"
              >
                {t.nav.teacherAccount}
                <ArrowRight className="ml-2 h-4 w-4 text-background" />
              </Button>
              {/* Hover opens the branch menu on desktop; the trigger also
                  toggles on click so touch devices (no hover) still work. */}
              <div
                ref={studentMenuRef}
                className="relative"
                onPointerEnter={(e) => {
                  if (e.pointerType === "mouse") setStudentOpen(true);
                }}
                onPointerLeave={(e) => {
                  if (e.pointerType === "mouse") setStudentOpen(false);
                }}
              >
                <Button
                  size="lg"
                  variant="outline"
                  // Opens only — never toggles. A mouse hover has already opened
                  // the menu by the time the click lands, so toggling here would
                  // close it out from under the pointer.
                  onClick={() => setStudentOpen(true)}
                  aria-expanded={studentOpen}
                  aria-haspopup="menu"
                  className="w-full border-2 border-foreground/20 bg-background text-foreground hover:bg-secondary hover:border-foreground/40 transition-colors text-sm md:text-base px-5 py-3 h-auto whitespace-nowrap"
                >
                  {t.nav.tryAsStudent}
                  <ChevronDown
                    className={`ml-2 h-4 w-4 transition-transform ${studentOpen ? "rotate-180" : ""}`}
                  />
                </Button>
                {studentOpen && (
                  <div
                    role="menu"
                    className="absolute left-0 right-0 sm:right-auto top-full pt-2 z-30 sm:min-w-[268px]"
                  >
                    <div className="bg-background border border-border rounded-xl shadow-lg p-2">
                      {studentBranches.map((b) => (
                        <button
                          key={b.key}
                          role="menuitem"
                          onClick={() => openStudentDemo(b.key)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/60 transition-colors text-left"
                        >
                          <span className={`${b.iconBg} rounded-lg p-2 flex-shrink-0`}>
                            <b.Icon className="h-4 w-4" />
                          </span>
                          <span className="font-semibold text-sm text-foreground">
                            {b.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-full">
            <div className="relative w-full overflow-hidden rounded-lg shadow-lg pb-[calc(61.5276%_+_41px)]">
              <iframe
                src="https://demo.arcade.software/EDU9cYg8H1Bb30azZf6v?embed&embed_mobile=inline&embed_desktop=inline&show_copy_link=true"
                title="Thiết lập lịch giao bài tự động cho chương trình Movers"
                loading="lazy"
                allowFullScreen
                allow="clipboard-write"
                className="absolute inset-0 w-full h-full"
                style={{
                  colorScheme: "light",
                  border: "none",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Decorative element */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-[600px] h-[300px] bg-primary/10 rounded-full blur-3xl"
      ></div>
    </section>
  );
};

export default Hero;
